import type { UseNavigateResult } from '@tanstack/react-router'
import { useEffect, useEffectEvent } from 'react'

import { salesOrderAsideViews } from '@/lib/services/sales-orders/sales-order.schema'

type SalesOrderAsideSearchValue = (typeof salesOrderAsideViews)[keyof typeof salesOrderAsideViews]

export type SalesOrderDeepLinkAsideView =
  | typeof salesOrderAsideViews.editRecipient
  | typeof salesOrderAsideViews.editFulfillmentDestination

function isFulfillmentDestinationAside(aside: SalesOrderAsideSearchValue | undefined): boolean {
  return (
    aside === salesOrderAsideViews.editFulfillmentAddress ||
    aside === salesOrderAsideViews.editFulfillmentDestination
  )
}

type InitialSalesOrderAsideViewOptions = {
  aside: SalesOrderAsideSearchValue | undefined
  canOpenDestinationAside: boolean
  canOpenRecipientAside: boolean
}

export function getInitialSalesOrderAsideView({
  aside,
  canOpenDestinationAside,
  canOpenRecipientAside,
}: InitialSalesOrderAsideViewOptions): SalesOrderDeepLinkAsideView | null {
  if (aside === salesOrderAsideViews.editRecipient && canOpenRecipientAside) {
    return salesOrderAsideViews.editRecipient
  }

  if (isFulfillmentDestinationAside(aside) && canOpenDestinationAside) {
    return salesOrderAsideViews.editFulfillmentDestination
  }

  if (aside === salesOrderAsideViews.editRecipient || isFulfillmentDestinationAside(aside)) {
    return null
  }

  return null
}

type SalesOrderAsideDeepLinkOptions = InitialSalesOrderAsideViewOptions & {
  isReadyToResolveAside?: boolean
  navigate: UseNavigateResult<'/$orgId/sales-orders/$salesOrderId'>
  setIsAsideOpen: (isAsideOpen: boolean) => void
  setView: (view: SalesOrderDeepLinkAsideView) => void
}

export function useSalesOrderAsideDeepLink({
  aside,
  canOpenDestinationAside,
  canOpenRecipientAside,
  isReadyToResolveAside = true,
  navigate,
  setIsAsideOpen,
  setView,
}: SalesOrderAsideDeepLinkOptions): void {
  const openDestinationAside = useEffectEvent(() => {
    setView(salesOrderAsideViews.editFulfillmentDestination)
    setIsAsideOpen(true)
  })
  const openRecipientAside = useEffectEvent(() => {
    setView(salesOrderAsideViews.editRecipient)
    setIsAsideOpen(true)
  })

  // The ?aside= deep link is one-shot: once the order is ready, open the aside it names (when
  // allowed) and drop the param from the URL so a refresh or back navigation doesn't reopen it.
  useEffect(() => {
    if (!isReadyToResolveAside) {
      return
    }

    if (aside === salesOrderAsideViews.editRecipient) {
      if (canOpenRecipientAside) {
        openRecipientAside()
      }

      void navigate({
        search: (prev) => ({ ...prev, aside: undefined }),
        ignoreBlocker: true,
        replace: true,
      })

      return
    }

    if (!isFulfillmentDestinationAside(aside)) {
      return
    }

    if (canOpenDestinationAside) {
      openDestinationAside()
    }

    void navigate({
      search: (prev) => ({ ...prev, aside: undefined }),
      ignoreBlocker: true,
      replace: true,
    })
  }, [aside, canOpenDestinationAside, canOpenRecipientAside, isReadyToResolveAside, navigate])
}
